import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  ChevronRight,
  Folder as FolderIcon,
  FolderOff as FolderOffIcon,
} from '@mui/icons-material';
import { useGetFolderItemsQuery, type FileStorageItem } from '../../api/fileStorageApi';
import { BaseDialog } from '../common';
import { TransferFolderNode } from './TransferFolderNode';

type PathItem = { id: string | undefined; name: string };

type TransferItemsDialogProps = {
  open: boolean;
  mode: 'move' | 'copy';
  currentPath: PathItem[];
  sourceItems: FileStorageItem[];
  onClose: () => void;
  onConfirm: (destinationPath: PathItem[]) => void;
};

const ROOT: PathItem = { id: undefined, name: 'Files' };

const getActionLabel = (mode: 'move' | 'copy') => (mode === 'move' ? 'Move to' : 'Copy to');

const initialExpandedIdsFromPath = (path: PathItem[]) =>
  new Set(path.map((item) => item.id).filter((id): id is string => Boolean(id)));

export const TransferItemsDialog = ({
  open,
  mode,
  currentPath,
  sourceItems,
  onClose,
  onConfirm,
}: TransferItemsDialogProps) => {
  const [destinationPath, setDestinationPath] = useState<PathItem[]>(() => [ROOT]);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(() =>
    initialExpandedIdsFromPath(currentPath)
  );
  const [isRootExpanded, setIsRootExpanded] = useState(true);
  const destinationFolderId = destinationPath[destinationPath.length - 1]?.id;
  const { isFetching } = useGetFolderItemsQuery(
    { nodeKey: destinationFolderId, storageType: 'All files' },
    { skip: !open }
  );
  const { data: rootData, isFetching: isRootFetching } = useGetFolderItemsQuery(
    { nodeKey: undefined, storageType: 'All files' },
    { skip: !open }
  );

  const rootFolders = useMemo(
    () => (rootData?.fileItems ?? []).filter((item) => item.isFolder),
    [rootData?.fileItems]
  );
  const sourceParentFolderId = currentPath[currentPath.length - 1]?.id;
  const disabledFolderIds = new Set<string>(
    [
      sourceParentFolderId,
      ...sourceItems.filter((item) => item.isFolder).map((item) => item.id),
    ].filter((id): id is string => Boolean(id))
  );
  const destinationIds = new Set(destinationPath.map((item) => item.id).filter(Boolean));
  const containsSourceFolderInDestination = sourceItems.some(
    (item) => item.isFolder && destinationIds.has(item.id)
  );
  const isDisabledDestination = Boolean(
    destinationFolderId && disabledFolderIds.has(destinationFolderId)
  );

  const actionLabel = getActionLabel(mode);
  const canConfirm = !isFetching && !containsSourceFolderInDestination && !isDisabledDestination;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={actionLabel}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => onConfirm(destinationPath)}
            disabled={!canConfirm}
          >
            {actionLabel}
          </Button>
        </>
      }
    >
      <Stack spacing={1}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            maxHeight: 280,
            overflow: 'auto',
            p: 0.25,
          }}
        >
          <List dense disablePadding>
            <ListItemButton
              dense
              selected={destinationFolderId === undefined}
              onClick={() => setDestinationPath([ROOT])}
              sx={{ pl: 1, pr: 0.5, py: 0.5 }}
            >
              <Box sx={{ width: 22, display: 'inline-flex', justifyContent: 'center', mr: 0.5 }}>
                {!isRootFetching && rootFolders.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsRootExpanded((prev) => !prev);
                    }}
                    sx={{
                      p: 0.25,
                      transition: 'transform 180ms ease',
                      transform: isRootExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  >
                    <ChevronRight fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <ListItemIcon sx={{ minWidth: 24 }}>
                <FolderIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText
                primary={ROOT.name}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
              />
            </ListItemButton>

            <Collapse in={isRootExpanded} unmountOnExit>
              {rootFolders.map((folder) => (
                <TransferFolderNode
                  key={folder.id}
                  folder={folder}
                  depth={1}
                  ancestors={[ROOT]}
                  destinationFolderId={destinationFolderId}
                  expandedFolderIds={expandedFolderIds}
                  disabledFolderIds={disabledFolderIds}
                  onToggleExpand={(folderId) =>
                    setExpandedFolderIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(folderId)) {
                        next.delete(folderId);
                      } else {
                        next.add(folderId);
                      }
                      return next;
                    })
                  }
                  onSelectDestination={setDestinationPath}
                />
              ))}
            </Collapse>
          </List>

          {!isRootFetching && rootFolders.length === 0 && (
            <Stack
              spacing={1}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                py: 3,
                px: 1.5,
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <FolderOffIcon fontSize="small" />
              <Typography variant="caption">There are no folders in Files yet.</Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </BaseDialog>
  );
};
